import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Play,
  FileText,
  Image,
  Music,
  Film,
  Upload,
  Trash2,
  Edit,
  Plus,
  Clock,
  BookOpen,
  Heart,
  Hand,
  Shield,
  Sparkles,
  Download,
  Eye,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${window.location.origin}/api`;

// Moduli del corso
const MODULI = [
  {
    id: "intro",
    numero: 1,
    titolo: "Introduzione al massaggio oncologico",
    durata: "15 minuti",
    descrizione: "Comprendere i principi fondamentali del massaggio oncologico, benefici e controindicazioni assolute/relative. L'importanza del consenso informato e della comunicazione con il team medico. Igiene e preparazione dell'ambiente per un tocco sicuro e rispettoso.",
    colore: "from-rose-100 to-rose-50",
    icona: BookOpen,
  },
  {
    id: "viso-collo",
    numero: 2,
    titolo: "Massaggio Oncologico: Viso e Collo",
    durata: "25 minuti",
    descrizione: "Tecniche di tocco leggero per alleviare tensioni su viso e collo. Approccio delicato a cicatrici, sensibilità cutanea e possibili linfedemi facciali o cervicali. Massaggio consapevole per favorire il rilassamento profondo.",
    colore: "from-purple-100 to-purple-50",
    icona: Sparkles,
  },
  {
    id: "braccia-mani",
    numero: 3,
    titolo: "Braccia e Mani (incluso Addome)",
    durata: "30 minuti",
    descrizione: "Focus sul linfedema post-chirurgico (es. dopo mastectomia) e tecniche di drenaggio linfatico manuale delicato per braccia e mani. Massaggio per alleviare neuropatie periferiche e migliorare la mobilità. Include un approccio estremamente delicato all'addome.",
    colore: "from-amber-100 to-amber-50",
    icona: Hand,
  },
  {
    id: "gambe-glutei",
    numero: 4,
    titolo: "Massaggio Oncologico: Gambe e Glutei",
    durata: "30 minuti",
    descrizione: "Gestione del linfedema agli arti inferiori con tecniche di drenaggio linfatico manuale. Massaggio per ridurre pesantezza, crampi e affaticamento alle gambe. Movimenti delicati per migliorare la circolazione e il benessere generale.",
    colore: "from-teal-100 to-teal-50",
    icona: Heart,
  },
  {
    id: "auto-trattamento",
    numero: 5,
    titolo: "Auto-Trattamento e Tocco per Caregiver",
    durata: "20 minuti",
    descrizione: "Tecniche semplici di auto-massaggio che il paziente può eseguire in autonomia. Istruzioni per familiari o caregiver su come offrire un tocco sicuro e confortante, promuovendo il legame e il benessere emotivo.",
    colore: "from-blue-100 to-blue-50",
    icona: Shield,
  },
];

// Tecniche specializzate
const TECNICHE = [
  {
    id: "drenaggio",
    titolo: "Drenaggio Linfatico Manuale",
    descrizione: "Tecniche specifiche per la gestione del linfedema post-chirurgico, con particolare attenzione alle zone più delicate e sensibili del corpo.",
    icona: Heart,
  },
  {
    id: "tocco",
    titolo: "Tocco Consapevole",
    descrizione: "Approccio terapeutico che considera le esigenze fisiche ed emotive del paziente, adattando pressione e modalità di contatto.",
    icona: Hand,
  },
  {
    id: "sicurezza",
    titolo: "Sicurezza e Prevenzione",
    descrizione: "Protocolli di igiene e sicurezza specifici per pazienti oncologici, con attenzione alle controindicazioni e alle precauzioni necessarie.",
    icona: Shield,
  },
];

const getFileIcon = (fileType) => {
  if (!fileType) return FileText;
  if (fileType.includes("video") || ["mp4", "avi", "mov", "wmv"].some(ext => fileType.includes(ext))) return Film;
  if (fileType.includes("audio") || ["mp3", "wav", "ogg"].some(ext => fileType.includes(ext))) return Music;
  if (fileType.includes("image") || ["jpg", "jpeg", "png", "gif", "webp"].some(ext => fileType.includes(ext))) return Image;
  if (fileType.includes("pdf")) return FileText;
  if (fileType.includes("powerpoint") || fileType.includes("ppt")) return FileText;
  if (fileType.includes("excel") || fileType.includes("xls")) return FileText;
  if (fileType.includes("word") || fileType.includes("doc")) return FileText;
  return FileText;
};

const MaterialiDocente = () => {
  const { user } = useAuth();
  const [materiali, setMateriali] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [selectedModulo, setSelectedModulo] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    modulo_id: "",
    file: null,
  });

  const isAdmin = user?.role === "admin";
  const isDocente = user?.role === "docente";
  const canEdit = isAdmin; // Solo admin può modificare

  useEffect(() => {
    fetchMateriali();
  }, []);

  const fetchMateriali = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/docente/materiali`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMateriali(data);
      }
    } catch (error) {
      console.error("Errore caricamento materiali:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Max 100MB
      if (file.size > 100 * 1024 * 1024) {
        toast.error("File troppo grande. Massimo 100MB");
        return;
      }
      setFormData({ ...formData, file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.modulo_id) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    if (!editingMaterial && !formData.file) {
      toast.error("Seleziona un file da caricare");
      return;
    }

    setUploading(true);
    const token = localStorage.getItem("token");

    try {
      let fileUrl = editingMaterial?.file_url;
      let fileType = editingMaterial?.file_type;

      // Upload file if new
      if (formData.file) {
        const uploadData = new FormData();
        uploadData.append("file", formData.file);

        const uploadRes = await fetch(`${API}/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: uploadData,
        });

        if (!uploadRes.ok) throw new Error("Errore upload file");
        const uploadResult = await uploadRes.json();
        fileUrl = uploadResult.url;
        fileType = formData.file.type || formData.file.name.split('.').pop();
      }

      // Save material
      const materialData = {
        name: formData.name,
        description: formData.description,
        modulo_id: formData.modulo_id,
        file_url: fileUrl,
        file_type: fileType,
      };

      const method = editingMaterial ? "PUT" : "POST";
      const url = editingMaterial 
        ? `${API}/docente/materiali/${editingMaterial.material_id}`
        : `${API}/docente/materiali`;

      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(materialData),
      });

      if (res.ok) {
        toast.success(editingMaterial ? "Materiale aggiornato!" : "Materiale caricato!");
        setDialogOpen(false);
        resetForm();
        fetchMateriali();
      } else {
        throw new Error("Errore salvataggio");
      }
    } catch (error) {
      toast.error("Errore: " + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (materialId) => {
    if (!window.confirm("Eliminare questo materiale?")) return;

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/docente/materiali/${materialId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        toast.success("Materiale eliminato");
        fetchMateriali();
      }
    } catch (error) {
      toast.error("Errore eliminazione");
    }
  };

  const resetForm = () => {
    setFormData({ name: "", description: "", modulo_id: "", file: null });
    setEditingMaterial(null);
  };

  const openEditDialog = (material) => {
    setEditingMaterial(material);
    setFormData({
      name: material.name,
      description: material.description || "",
      modulo_id: material.modulo_id,
      file: null,
    });
    setDialogOpen(true);
  };

  const getMaterialiByModulo = (moduloId) => {
    return materiali.filter(m => m.modulo_id === moduloId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#B87A8A] to-[#a06978] text-white py-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">
            ODR Materiale Didattico
          </h1>
          <p className="text-rose-100 text-lg">
            Corsi di Formazione in Estetica Oncologica
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Admin Controls */}
        {canEdit && (
          <div className="mb-8 flex justify-end">
            <Button
              onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}
              className="bg-[#B87A8A] hover:bg-[#a06978] rounded-full"
              data-testid="add-material-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Carica Nuovo Materiale
            </Button>
          </div>
        )}

        {/* Moduli */}
        <div className="space-y-8">
          {MODULI.map((modulo) => {
            const moduloMateriali = getMaterialiByModulo(modulo.id);
            const IconComponent = modulo.icona;

            return (
              <div
                key={modulo.id}
                className={`bg-gradient-to-r ${modulo.colore} rounded-2xl overflow-hidden shadow-lg`}
              >
                {/* Modulo Header */}
                <div className="p-6 border-b border-white/50">
                  <div className="flex items-start gap-4">
                    <div className="bg-white/50 p-3 rounded-xl">
                      <IconComponent className="w-8 h-8 text-[#B87A8A]" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-[#B87A8A] font-bold text-xl">
                          {modulo.numero})
                        </span>
                        <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                          {modulo.titolo}
                        </h2>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600 mb-3">
                        <Clock className="w-4 h-4" />
                        <span className="font-medium">Durata: {modulo.durata}</span>
                      </div>
                      <p className="text-gray-700 leading-relaxed">
                        {modulo.descrizione}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Materiali del Modulo */}
                <div className="p-6 bg-white/30">
                  {moduloMateriali.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {moduloMateriali.map((material) => {
                        const FileIcon = getFileIcon(material.file_type);
                        return (
                          <div
                            key={material.material_id}
                            className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-start gap-3">
                              <div className="bg-rose-100 p-2 rounded-lg">
                                <FileIcon className="w-6 h-6 text-[#B87A8A]" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-gray-800 truncate">
                                  {material.name}
                                </h4>
                                {material.description && (
                                  <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                    {material.description}
                                  </p>
                                )}
                                <div className="flex items-center gap-2 mt-3">
                                  <a
                                    href={material.file_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#B87A8A] hover:text-[#a06978] text-sm flex items-center gap-1"
                                  >
                                    {material.file_type?.includes("video") ? (
                                      <>
                                        <Play className="w-4 h-4" /> Riproduci
                                      </>
                                    ) : (
                                      <>
                                        <Eye className="w-4 h-4" /> Visualizza
                                      </>
                                    )}
                                  </a>
                                  <a
                                    href={material.file_url}
                                    download
                                    className="text-gray-500 hover:text-gray-700 text-sm flex items-center gap-1"
                                  >
                                    <Download className="w-4 h-4" />
                                  </a>
                                  {canEdit && (
                                    <>
                                      <button
                                        onClick={() => openEditDialog(material)}
                                        className="text-blue-500 hover:text-blue-700 text-sm"
                                      >
                                        <Edit className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => handleDelete(material.material_id)}
                                        className="text-red-500 hover:text-red-700 text-sm"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Film className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Nessun materiale caricato per questo modulo</p>
                      {canEdit && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3 rounded-full"
                          onClick={() => {
                            setFormData({ ...formData, modulo_id: modulo.id });
                            setDialogOpen(true);
                          }}
                        >
                          <Plus className="w-4 h-4 mr-1" /> Aggiungi
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Tecniche Specializzate */}
        <div className="mt-12">
          <div className="text-center mb-8">
            <p className="text-[#B87A8A] font-medium italic mb-2">Tecniche Specializzate</p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800">
              COMPETENZE AVANZATE NEL MASSAGGIO ONCOLOGICO
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TECNICHE.map((tecnica) => {
              const IconComponent = tecnica.icona;
              return (
                <div
                  key={tecnica.id}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    <div className="text-[#B87A8A]">
                      <IconComponent className="w-10 h-10" />
                    </div>
                    <div>
                      <h3 className="text-[#B87A8A] font-bold text-lg italic mb-2">
                        {tecnica.titolo}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {tecnica.descrizione}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Dialog Upload */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingMaterial ? "Modifica Materiale" : "Carica Nuovo Materiale"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome Materiale *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="es. Video introduttivo"
                className="rounded-xl"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="modulo">Modulo *</Label>
              <Select
                value={formData.modulo_id}
                onValueChange={(value) => setFormData({ ...formData, modulo_id: value })}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder="Seleziona modulo" />
                </SelectTrigger>
                <SelectContent>
                  {MODULI.map((modulo) => (
                    <SelectItem key={modulo.id} value={modulo.id}>
                      {modulo.numero}) {modulo.titolo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrizione</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Breve descrizione del contenuto..."
                className="rounded-xl"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="file">
                File {!editingMaterial && "*"}
                <span className="text-xs text-gray-500 ml-2">
                  (Video, Audio, PDF, Word, Excel, PowerPoint, Immagini - Max 100MB)
                </span>
              </Label>
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-[#B87A8A] transition-colors">
                <input
                  type="file"
                  id="file"
                  onChange={handleFileChange}
                  className="hidden"
                  accept="video/*,audio/*,image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                />
                <label htmlFor="file" className="cursor-pointer">
                  <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  {formData.file ? (
                    <p className="text-[#B87A8A] font-medium">{formData.file.name}</p>
                  ) : (
                    <p className="text-gray-500">Clicca per selezionare un file</p>
                  )}
                </label>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setDialogOpen(false);
                  resetForm();
                }}
                className="flex-1 rounded-full"
              >
                Annulla
              </Button>
              <Button
                type="submit"
                disabled={uploading}
                className="flex-1 bg-[#B87A8A] hover:bg-[#a06978] rounded-full"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Caricamento...
                  </>
                ) : editingMaterial ? (
                  "Salva Modifiche"
                ) : (
                  "Carica Materiale"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaterialiDocente;
